/**
 * Generato automaticamente.
 * framework-cmp-domande
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type { PostFrontFileBody } from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Aggiungi un file statico
 * @summary Aggiungi File statico
 */
export const postFrontFile = (
  postFrontFileBody: PostFrontFileBody,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  const formData = new FormData();
  formData.append('file', postFrontFileBody.file);

  return apiClientRequest<string>(
    {
      url: `/framework-cmp-domande/file`,
      method: 'post',
      headers: { 'Content-Type': 'multipart/form-data' },
      data: formData,
    },
    options,
  );
};

export const getPostFrontFileMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontFile>>,
    TError,
    { data: PostFrontFileBody },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postFrontFile>>,
  TError,
  { data: PostFrontFileBody },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postFrontFile>>,
    { data: PostFrontFileBody }
  > = props => {
    const { data } = props ?? {};

    return postFrontFile(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostFrontFileMutationResult = NonNullable<
  Awaited<ReturnType<typeof postFrontFile>>
>;
export type PostFrontFileMutationBody = PostFrontFileBody;
export type PostFrontFileMutationError = ErrorType<unknown>;

/**
 * @summary Aggiungi File statico
 */
export const usePostFrontFile = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontFile>>,
    TError,
    { data: PostFrontFileBody },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostFrontFileMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Permette il download di un file statico
 * @summary Recupera File Caricato
 */
export const getFrontFilePathNomeFile = (
  path: string,
  nomeFile: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<string[]>(
    {
      url: `/framework-cmp-domande/file/${path}/${nomeFile}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontFilePathNomeFileQueryKey = (
  path: string,
  nomeFile: string,
) => [`/framework-cmp-domande/file/${path}/${nomeFile}`] as const;

export const getGetFrontFilePathNomeFileQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontFilePathNomeFile>>,
  TError = ErrorType<unknown>,
>(
  path: string,
  nomeFile: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontFilePathNomeFile>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontFilePathNomeFile>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetFrontFilePathNomeFileQueryKey(path, nomeFile);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontFilePathNomeFile>>
  > = ({ signal }) =>
    getFrontFilePathNomeFile(path, nomeFile, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!(path && nomeFile), ...queryOptions };
};

export type GetFrontFilePathNomeFileQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontFilePathNomeFile>>
>;
export type GetFrontFilePathNomeFileQueryError = ErrorType<unknown>;

/**
 * @summary Recupera File Caricato
 */
export const useGetFrontFilePathNomeFile = <
  TData = Awaited<ReturnType<typeof getFrontFilePathNomeFile>>,
  TError = ErrorType<unknown>,
>(
  path: string,
  nomeFile: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontFilePathNomeFile>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontFilePathNomeFileQueryOptions(
    path,
    nomeFile,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
